import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import {
  Plus, Trash2, Save, Users, MapPin, Loader2, X, UserPlus, ChevronDown, ChevronUp,
  ToggleLeft, ToggleRight, Shield,
} from 'lucide-react';

interface SalesTeam {
  id: string;
  name: string;
  area: string | null;
  isActive: boolean;
  managerId: string | null;
  manager: { id: string; firstName: string; lastName: string } | null;
  members: TeamMember[];
  _count?: { members: number };
}

interface TeamMember {
  id: string;
  userId: string;
  role: string;
  user: { id: string; firstName: string; lastName: string; email: string; phone?: string; designation?: string; location?: string };
}

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const TEAM_ROLES = [
  { value: 'backend_crm', label: 'Backend CRM' },
  { value: 'field_manager', label: 'Field Manager' },
  { value: 'field_executive', label: 'Field Executive' },
];

export function SalesTeamsTab() {
  const [teams, setTeams] = useState<SalesTeam[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teamModeEnabled, setTeamModeEnabled] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamArea, setNewTeamArea] = useState('');
  const [newTeamManagerId, setNewTeamManagerId] = useState('');
  // Add member modal
  const [addMemberTeamId, setAddMemberTeamId] = useState<string | null>(null);
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [addMemberRole, setAddMemberRole] = useState('field_executive');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [teamsRes, usersRes, modeRes] = await Promise.all([
        api.get('/sales-teams'),
        api.get('/employees'),
        api.get('/sales-teams/mode'),
      ]);
      setTeams(teamsRes.data.data || []);
      setUsers((usersRes.data.data || []).map((e: any) => ({
        id: e.userId || e.id,
        firstName: e.firstName,
        lastName: e.lastName,
        email: e.email,
      })));
      setTeamModeEnabled(modeRes.data.data?.enabled || false);
    } catch (err) {
      console.error('Failed to load sales teams', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleTeamMode = async () => {
    try {
      const res = await api.put('/sales-teams/mode', { enabled: !teamModeEnabled });
      setTeamModeEnabled(res.data.data?.enabled || false);
    } catch (err) {
      console.error('Failed to toggle team mode', err);
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    setSaving(true);
    try {
      await api.post('/sales-teams', {
        name: newTeamName.trim(),
        area: newTeamArea.trim() || null,
        managerId: newTeamManagerId || null,
      });
      setNewTeamName('');
      setNewTeamArea('');
      setNewTeamManagerId('');
      setShowAddTeam(false);
      await loadData();
    } catch (err) {
      console.error('Failed to create team', err);
    } finally {
      setSaving(false);
    }
  };

  const deleteTeam = async (teamId: string) => {
    if (!confirm('Deactivate this team?')) return;
    try {
      await api.delete(`/sales-teams/${teamId}`);
      await loadData();
    } catch (err) {
      console.error('Failed to delete team', err);
    }
  };

  const addMember = async () => {
    if (!addMemberTeamId || !addMemberUserId) return;
    setSaving(true);
    try {
      await api.post(`/sales-teams/${addMemberTeamId}/members`, {
        userId: addMemberUserId,
        role: addMemberRole,
      });
      setAddMemberTeamId(null);
      setAddMemberUserId('');
      setAddMemberRole('field_executive');
      await loadData();
    } catch (err) {
      console.error('Failed to add member', err);
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (teamId: string, memberId: string) => {
    try {
      await api.delete(`/sales-teams/${teamId}/members/${memberId}`);
      await loadData();
    } catch (err) {
      console.error('Failed to remove member', err);
    }
  };

  const updateMemberRole = async (teamId: string, memberId: string, role: string) => {
    try {
      await api.put(`/sales-teams/${teamId}/members/${memberId}`, { role });
      await loadData();
    } catch (err) {
      console.error('Failed to update member role', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Sales Teams</h2>
          <p className="text-sm text-gray-500">Manage sales teams, assign members, and control lead visibility.</p>
        </div>
        <button
          onClick={() => setShowAddTeam(true)}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={16} /> New Team
        </button>
      </div>

      {/* Team Mode Toggle */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center gap-3">
          <Shield size={20} className="text-[var(--ink-0)]" />
          <div>
            <p className="text-sm font-medium text-gray-900">Sales Team Mode</p>
            <p className="text-xs text-gray-500">
              When enabled, managers/backend CRM see all team leads. Executives see only their assigned leads.
            </p>
          </div>
        </div>
        <button onClick={toggleTeamMode} className="text-gray-600 hover:text-[var(--ink-0)]">
          {teamModeEnabled ? <ToggleRight size={32} className="text-[var(--ink-0)]" /> : <ToggleLeft size={32} />}
        </button>
      </div>

      {/* Add Team Form */}
      {showAddTeam && (
        <div className="rounded-lg border border-[var(--ink-3)] bg-[var(--saffron-50)] p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Create New Team</h3>
            <button onClick={() => setShowAddTeam(false)}><X size={16} className="text-gray-400" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="Team Name *"
              value={newTeamName}
              onChange={e => setNewTeamName(e.target.value)}
            />
            <input
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              placeholder="Area (e.g. Delhi NCR)"
              value={newTeamArea}
              onChange={e => setNewTeamArea(e.target.value)}
            />
            <select
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={newTeamManagerId}
              onChange={e => setNewTeamManagerId(e.target.value)}
            >
              <option value="">Select Manager</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end">
            <button
              onClick={createTeam}
              disabled={saving || !newTeamName.trim()}
              className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create
            </button>
          </div>
        </div>
      )}

      {/* Teams List */}
      {teams.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          No sales teams created yet. Click "New Team" to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {teams.map(team => (
            <div key={team.id} className="rounded-lg border border-gray-200 bg-white">
              {/* Team Header */}
              <div
                className="flex cursor-pointer items-center justify-between px-4 py-3 hover:bg-gray-50"
                onClick={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
              >
                <div className="flex items-center gap-3">
                  <Users size={18} className="text-[var(--ink-0)]" />
                  <div>
                    <span className="font-medium text-gray-900">{team.name}</span>
                    {team.area && (
                      <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-500">
                        <MapPin size={12} /> {team.area}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">{team.members?.length || 0} members</span>
                  {team.manager && (
                    <span className="rounded-full bg-[var(--saffron-50)] px-2 py-0.5 text-xs text-[var(--ink-0)]">
                      Mgr: {team.manager.firstName} {team.manager.lastName}
                    </span>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); deleteTeam(team.id); }} className="text-gray-400 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                  {expandedTeam === team.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* Team Members (expanded) */}
              {expandedTeam === team.id && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-2">
                  {/* Add Member Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => setAddMemberTeamId(team.id)}
                      className="flex items-center gap-1 text-xs text-[var(--ink-0)] hover:text-[var(--ink-0)]"
                    >
                      <UserPlus size={14} /> Add Member
                    </button>
                  </div>

                  {/* Member rows */}
                  {team.members?.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500">
                          <th className="py-1 font-medium">Name</th>
                          <th className="py-1 font-medium">Email</th>
                          <th className="py-1 font-medium">Designation</th>
                          <th className="py-1 font-medium">Role</th>
                          <th className="py-1 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {team.members.map(m => (
                          <tr key={m.id} className="border-t border-gray-50">
                            <td className="py-1.5 text-gray-900">{m.user.firstName} {m.user.lastName}</td>
                            <td className="py-1.5 text-gray-600">{m.user.email}</td>
                            <td className="py-1.5 text-gray-600">{m.user.designation || '—'}</td>
                            <td className="py-1.5">
                              <select
                                className="rounded border border-gray-200 px-2 py-0.5 text-xs"
                                value={m.role}
                                onChange={e => updateMemberRole(team.id, m.id, e.target.value)}
                              >
                                {TEAM_ROLES.map(r => (
                                  <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                              </select>
                            </td>
                            <td className="py-1.5">
                              <button onClick={() => removeMember(team.id, m.id)} className="text-gray-400 hover:text-red-500">
                                <X size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="py-2 text-center text-xs text-gray-400">No members yet</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Member Modal */}
      {addMemberTeamId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Add Team Member</h3>
              <button onClick={() => setAddMemberTeamId(null)}><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">User</label>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={addMemberUserId}
                  onChange={e => setAddMemberUserId(e.target.value)}
                >
                  <option value="">Select user...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Team Role</label>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={addMemberRole}
                  onChange={e => setAddMemberRole(e.target.value)}
                >
                  {TEAM_ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setAddMemberTeamId(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addMember}
                disabled={saving || !addMemberUserId}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
